import { S3_URL } from '@constants/generic';
import { RoleType } from '@features/team-management/constants';

import { TRIAL_MOCK_IDS } from '../constants';
import {
  ALL_GROUPS,
  ALL_MEMBERS,
  ALL_TRANSACTIONS,
  FIRST_NAMES,
  getGroupsForBranch,
  LAST_NAMES
} from './generator';

const BANK_LOGOS = {
  SBI: `${S3_URL}/bank_logo/sbi.png`,
  HDFC: `${S3_URL}/bank_logo/hdfc.png`
};

// ── Groups (shared across group list + member filters) ──

export const getGroupsListMock = (
  page: number,
  limit: number,
  status: string,
  branchId?: string
) => {
  const branchGroups = getGroupsForBranch(branchId);
  const filtered = status === 'inactive' ? [] : branchGroups;
  const start = page * limit;
  const slice = filtered.slice(start, start + limit);
  return {
    data: slice,
    pagination: {
      total_rows: filtered.length,
      page,
      limit,
      last_page: start + limit >= filtered.length
    }
  };
};

export const getGroupsWithoutPaginationMock = (branchId?: string) =>
  getGroupsForBranch(branchId);

export const getGroupDetailMock = (groupId: string) => {
  const group = ALL_GROUPS.find((g: any) => g.id === groupId) || ALL_GROUPS[0];
  return {
    id: group.id,
    name: group.name,
    amount: group.amount,
    frequency: group.interval,
    interval_frequency: group.intervalFrequency,
    is_active: group.is_active,
    is_payment_link_sent: group.is_payment_link_sent,
    is_split_payment: group.is_split_payment,
    is_fee_configured: false,
    start_date: group.collection_started_at,
    next_due_at: group.next_due_at,
    interval_execution_type: 'scheduled',
    member_count: group.member_count,
    active_member_count: group.active_member_count,
    image_url: '',
    payment_schedule: {
      interval: group.interval,
      monthly_config: null,
      weekly_config: null,
      term_details: null
    },
    collection_started_at: group.collection_started_at,
    is_attendance_tracking_enabled: false,
    is_attendance_marked: false,
    group_fee_amount_configuration: [],
    payment_stats: {
      pay_received: group.pay_received,
      pay_due: group.pay_due,
      pending_count: group.pending_count
    },
    payment_list: (() => {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];
      const yr = new Date().getFullYear();
      const perMonth = parseInt(group.pay_received, 10) / 12;
      const duePerMonth = parseInt(group.pay_due, 10) / 12;
      return months.map((m) => ({
        payment_month: m,
        payment_year: yr,
        payment_due: duePerMonth.toFixed(2),
        payment_received: perMonth.toFixed(2)
      }));
    })()
  };
};

// ── Members ──

type MockMember = (typeof ALL_MEMBERS)[number];

// Live, mutable copy so demo actions (eg. creating a Quick Collect link) can
// add rows for the rest of the session. Resets on page reload.
let trialMembers: MockMember[] = [...ALL_MEMBERS];

export const getMembersMock = (
  page: number,
  limit: number,
  search: string,
  groupId: string,
  branchId?: string
) => {
  let filtered = branchId
    ? trialMembers.filter((m) => m.branch_id === branchId)
    : trialMembers;

  if (search) {
    const term = search.toLowerCase();
    filtered = filtered.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.mobile.includes(term) ||
        m.admission_number.toLowerCase().includes(term)
    );
  }

  if (groupId) {
    const ids = groupId.split(',');
    filtered = filtered.filter((m) =>
      m.groups.some((g: any) => ids.includes(g.id))
    );
  }

  const start = page * limit;
  const slice = filtered.slice(start, start + limit);
  return {
    data: slice,
    pagination: {
      total_rows: filtered.length,
      page,
      limit
    }
  };
};

// ── Transactions ──

type MockTransaction = (typeof ALL_TRANSACTIONS)[number];

// Live, mutable copy so demo actions (eg. creating a Quick Collect link) can
// add rows for the rest of the session. Resets on page reload.
let trialTransactions: MockTransaction[] = [...ALL_TRANSACTIONS];

let temporaryTransactionCount = 0;
let temporaryMemberCount = 0;

// Finds the member a Quick Collect link was sent to (by mobile number), or
// creates a new one so the recipient shows up in the Members list too.
const findOrCreateTrialMember = ({
  name,
  mobile,
  countryCode,
  groupId
}: {
  name: string;
  mobile?: string;
  countryCode?: string;
  groupId?: string;
}): MockMember => {
  const existing = mobile
    ? trialMembers.find((m) => m.mobile === mobile)
    : undefined;
  if (existing) return existing;

  const group = groupId
    ? ALL_GROUPS.find((g: any) => g.id === groupId)
    : undefined;
  temporaryMemberCount += 1;
  const newMemberId = `trial-qc-member-${temporaryMemberCount}`;
  const newMember: MockMember = {
    id: newMemberId,
    member_id: newMemberId,
    branch_id: group?.branch_id || TRIAL_MOCK_IDS.BRANCH_ID,
    branch_name: group?.branch_name || TRIAL_MOCK_IDS.BRANCH_NAME,
    name,
    country_code: countryCode || '91',
    mobile: mobile || '',
    primary_country_code: countryCode || '91',
    primary_mobile_number: mobile || '',
    groups: group ? [{ id: group.id, name: group.name, is_active: true }] : [],
    guardian_name: '',
    admission_number: `ACM-QC-${temporaryMemberCount}`,
    admission_date: '',
    dob: '',
    email: '',
    secondary_country_code: '91',
    secondary_mobile_number: '',
    address_line_1: '',
    address_line_2: '',
    city: 'Bengaluru',
    state: 'Karnataka',
    country: 'India',
    pincode: '560001',
    is_active: true,
    total_due: undefined,
    total_collected: '0',
    processing: []
  };
  trialMembers = [...trialMembers, newMember];
  return newMember;
};

export const addTemporaryTransaction = ({
  name,
  mobile,
  countryCode,
  amount,
  note,
  groupId
}: {
  name: string;
  mobile?: string;
  countryCode?: string;
  amount: string;
  note?: string;
  groupId?: string;
}) => {
  const member = findOrCreateTrialMember({
    name,
    mobile,
    countryCode,
    groupId
  });
  const group = groupId
    ? ALL_GROUPS.find((g: any) => g.id === groupId)
    : undefined;

  temporaryTransactionCount += 1;
  const newTransaction: MockTransaction = {
    id: `trial-qc-${temporaryTransactionCount}`,
    branch_id: member.branch_id,
    branch_name: member.branch_name,
    remind_again: true,
    payment_link: 'https://pay.cofee.life/trial',
    type: group ? 'group' : 'individual',
    order_short_url: '',
    receipt_url: '',
    payment_description: '',
    payment_to: TRIAL_MOCK_IDS.ORG_NAME,
    disabled_at: null,
    name: member.name,
    member_id: member.id,
    member_phone_number: `+${member.country_code}${member.mobile}`,
    note: note || '',
    amount,
    total_amount: amount,
    original_amount_requested: amount,
    original_total_amount_requested: amount,
    status: 'processing',
    settlement_status: '',
    payment_mode: '',
    payment_method: '',
    paid_at: null,
    due_at: new Date().toISOString(),
    settled_at: '',
    total_settled_amount: '0',
    total_paid_amount: '0',
    is_split_payment: false,
    utr: '',
    group_id: group?.id || '',
    group_name: group?.name || ''
  };
  trialTransactions = [newTransaction, ...trialTransactions];

  // Keep the recipient's Total Due / due breakdown in sync with this new send.
  member.total_due = String((Number(member.total_due) || 0) + Number(amount));
  member.processing = [
    {
      amount,
      paid_on: newTransaction.due_at,
      payment_order_id: newTransaction.id
    },
    ...member.processing
  ];

  return newTransaction;
};

export const getTransactionsMock = (
  status: string,
  page: number,
  limit: number,
  memberId?: string,
  branchId?: string
) => {
  let filtered = branchId
    ? trialTransactions.filter((t) => t.branch_id === branchId)
    : trialTransactions;

  if (status === 'paid') {
    filtered = filtered.filter((t) => t.status === 'success');
  } else if (status === 'pending') {
    filtered = filtered.filter(
      (t) => t.status === 'processing' && !t.is_split_payment
    );
  } else if (status === 'partially_paid') {
    filtered = filtered.filter((t) => t.is_split_payment);
  }

  if (memberId) {
    filtered = filtered.filter((t) => t.member_id === memberId);
  }

  const start = page * limit;
  const slice = filtered.slice(start, start + limit);
  return {
    data: slice.length > 0 ? slice : null,
    pagination: {
      total_rows: filtered.length,
      page,
      limit
    }
  };
};

// ── Team Management (org-level users) ──

const ORG_MEMBER_ROLES = [
  RoleType.ORGANISATION_ADMIN,
  RoleType.BRANCH_ADMIN,
  RoleType.GROUP_ADMIN,
  RoleType.FINANCE_ADMIN,
  RoleType.ORGANISATION_ADMIN
];

const getGroupNames = (i: number) => {
  if (i === 2) return [ALL_GROUPS[0].name, ALL_GROUPS[1].name];
  if (i === 3) return [ALL_GROUPS[2].name];
  return null;
};

const MOCK_ORG_MEMBERS = Array.from({ length: 5 }, (_, i) => ({
  user_id: `trial-orguser-${String(i + 1).padStart(3, '0')}`,
  user_name: `${FIRST_NAMES[i]} ${LAST_NAMES[i]}`,
  mobile: `99${String(80000000 + i * 1111111).slice(0, 8)}`,
  role_name: ORG_MEMBER_ROLES[i],
  status: 'active',
  user_organisation_role_id: `trial-uor-${String(i + 1).padStart(3, '0')}`,
  branch_name: i < 3 ? [TRIAL_MOCK_IDS.BRANCH_NAME] : null,
  group_name: getGroupNames(i)
}));

export const getOrgMembersMock = (page: number, limit: number) => {
  const start = page * limit;
  const slice = MOCK_ORG_MEMBERS.slice(start, start + limit);
  return {
    data: slice,
    pagination: {
      total_rows: MOCK_ORG_MEMBERS.length,
      page,
      limit
    }
  };
};

export const getInvitedMembersMock = () => ({
  data: [],
  pagination: { total_rows: 0, page: 0, limit: 10 }
});

// ── Member create / update (POST) ──

export const createMemberMock = () => ({
  data: {
    member_response: [{ status: 'success', errors: [] }],
    warning_records: []
  }
});

// Shared by the members list and the member detail mock so the Amount /
// Next Requested date / payment schedule shown for a member always agree,
// whichever screen they're viewed from.
const getGroupFeeContextForMember = (member: MockMember, groupId?: string) => {
  const group = ALL_GROUPS.find((g) => g.id === groupId);
  const nextPendingTransaction = trialTransactions
    .filter(
      (t) =>
        t.member_id === member.id &&
        t.group_id === groupId &&
        t.status === 'processing'
    )
    .sort(
      (a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime()
    )[0];

  return {
    group,
    nextDueAt: nextPendingTransaction?.due_at || group?.next_due_at
  };
};

// ── Member detail (GET by ID) ──

export const getMemberDetailMock = (memberId: string, groupId?: string) => {
  const member = trialMembers.find((m) => m.id === memberId) || trialMembers[0];
  const resolvedGroupId = groupId || member.groups[0]?.id;
  const { group, nextDueAt } = getGroupFeeContextForMember(
    member,
    resolvedGroupId
  );

  return {
    ...member,
    fee_amount: group?.amount,
    amount: group?.amount,
    total_amount: group?.amount,
    next_due_at: nextDueAt,
    payment_schedule: group
      ? {
          interval: group.interval,
          interval_frequency: group.intervalFrequency,
          start_date: group.collection_started_at,
          end_date: undefined,
          monthly_config: { day_option: 'first_day' }
        }
      : undefined,
    payment_summary: {
      total_due_amount: Number(member.total_due) || 0,
      total_collected_amount: Number(member.total_collected) || 0,
      total_unsettled_amount: 0
    }
  };
};

// ── Group member contact numbers (Quick Collect tab) ──

export const getGroupMemberContactsMock = (groupId: string) =>
  trialMembers
    .filter((m) => m.groups.some((g: any) => g.id === groupId))
    .map((m) => ({
      name: m.name,
      country_code: m.country_code,
      mobile: m.mobile
    }));

// ── Group members list (Group Details > Members tab) ──

export const getGroupMembersMock = (
  groupId: string,
  page: number,
  limit: number,
  search: string
) => {
  const group = ALL_GROUPS.find((g) => g.id === groupId);

  let filtered = trialMembers.filter((m) =>
    m.groups.some((g: any) => g.id === groupId)
  );

  if (search) {
    const term = search.toLowerCase();
    filtered = filtered.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        m.mobile.includes(term) ||
        m.admission_number.toLowerCase().includes(term)
    );
  }

  const start = page * limit;
  const slice = filtered.slice(start, start + limit);

  // Enrich each member with this group's fee amount and their next unpaid
  // due date for it (falling back to the group's recurring due date once
  // they're fully paid up), so the Amount / Next Requested date columns
  // aren't blank in demo mode.
  const enrichedSlice = slice.map((member) => {
    const { nextDueAt } = getGroupFeeContextForMember(member, groupId);
    return {
      ...member,
      fee_amount: group?.amount,
      next_due_at: nextDueAt
    };
  });

  return {
    data: enrichedSlice,
    pagination: {
      total_rows: filtered.length,
      page,
      limit
    }
  };
};

// ── Transaction detail (Payment Details modal) ──

export const getTransactionDetailMock = (orderId: string) => {
  const txn = trialTransactions.find((t) => t.id === orderId);
  if (!txn) return trialTransactions[0];
  const member = trialMembers.find((m) => m.id === txn.member_id);
  return {
    ...txn,
    total_amount: txn.amount,
    closed_amount: '0',
    original_amount_requested: txn.amount,
    member_phone_number: member
      ? `+${member.country_code}${member.mobile}`
      : txn.member_phone_number || '',
    member_secondary_phone_number: '',
    fee_amount_configuration: [],
    fine_amount: '0',
    cgst_amount: '0',
    sgst_amount: '0',
    igst_amount: '0',
    cgst_percentage: '0',
    sgst_percentage: '0',
    igst_percentage: '0',
    total_gst: '0',
    total_discount_amount: '0',
    no_of_sessions: 0,
    cost_per_session: '0',
    is_auto_debit_in_progress: false
  };
};

// ── Payment order history (Transaction history in payment details) ──

export const getPaymentOrderHistoryMock = (orderId: string) => {
  const txn = trialTransactions.find((t) => t.id === orderId);
  if (!txn) return [];

  if (txn.status === 'pending') return [];

  const entries = [];

  if (txn.status === 'success') {
    entries.push({
      type: 'payment',
      payment_mode: txn.payment_mode,
      payment_method: txn.payment_method,
      paid_amount: txn.total_paid_amount,
      paid_at: txn.paid_at,
      settled_at: txn.settled_at,
      settlement_status: txn.settlement_status,
      transaction_id: txn.id,
      utr: txn.utr,
      payment_description: txn.payment_description
    });
  }

  if (txn.is_split_payment) {
    entries.push({
      type: 'payment',
      payment_mode: txn.payment_mode,
      payment_method: txn.payment_method,
      paid_amount: txn.total_paid_amount,
      paid_at: txn.paid_at,
      settled_at: '',
      settlement_status: '',
      transaction_id: `${txn.id}-split-1`,
      utr: '',
      payment_description: ''
    });
  }

  return entries;
};

// ── Account details (Branch Settings) ──

export const getAccountDetailsMock = () => ({
  branch: {
    id: 1,
    branch_id: TRIAL_MOCK_IDS.BRANCH_ID,
    organisation_id: TRIAL_MOCK_IDS.ORGANIZATION_ID,
    bank_account: {
      bank_account_name: 'Acme Academy',
      bank_account_number: '1234567890123456',
      ifsc: 'SBIN0001234',
      bank_name: 'State Bank of India',
      nick_name: 'Primary Account',
      status: 'verified',
      bank_logo: BANK_LOGOS.SBI
    },
    upi: []
  },
  organisation: {
    id: 2,
    organisation_id: TRIAL_MOCK_IDS.ORGANIZATION_ID,
    bank_account: null,
    upi: []
  },
  additionalAccounts: []
});

// ── Bank account list (Manage Bank Account) ──

const MOCK_BANK_ACCOUNTS = [
  {
    id: 1,
    bank_account_name: 'Acme Academy',
    bank_account_number: '1234567890123456',
    bank_branch_name: 'MG Road Branch',
    bank_ifsc: 'SBIN0001234',
    bank_logo: BANK_LOGOS.SBI,
    bank_name: 'State Bank of India',
    is_active: true,
    nick_name: 'Primary Account',
    merchant_reference_id: 'ACC-DEMO-001',
    status: 'approved'
  },
  {
    id: 2,
    bank_account_name: 'Acme Academy',
    bank_account_number: '9876543210987654',
    bank_branch_name: 'Koramangala Branch',
    bank_ifsc: 'HDFC0004321',
    bank_logo: BANK_LOGOS.HDFC,
    bank_name: 'HDFC Bank',
    is_active: true,
    nick_name: 'Secondary Account',
    merchant_reference_id: 'ACC-DEMO-002',
    status: 'approved'
  }
];

export const getBankAccountListMock = () => ({
  data: MOCK_BANK_ACCOUNTS,
  pagination: {
    total_rows: MOCK_BANK_ACCOUNTS.length
  }
});

export const getBankAccountDetailByIdMock = (bankId: string) => {
  const account = MOCK_BANK_ACCOUNTS.find((acc) => `${acc.id}` === bankId);
  if (!account) return null;

  return {
    organisation_bank_account_detail: {
      id: `${account.id}`,
      bank_account_number: account.bank_account_number,
      bank_account_name: account.bank_account_name,
      bank_ifsc: account.bank_ifsc,
      bank_name: account.bank_name,
      bank_branch_name: account.bank_branch_name,
      bank_logo: account.bank_logo,
      status: account.status,
      nick_name: account.nick_name,
      merchant_reference_id: account.merchant_reference_id,
      bank_ledger_name: account.bank_account_name,
      pan: '',
      remarks: ''
    },
    remarks: '',
    rejected_reason: '',
    upload_bank_document_response: {
      files: [],
      account_detail_id: `${account.id}`
    }
  };
};

export const getEsignDocumentsMock = () => ({
  documents: []
});
